import { FC, useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardFooter,
  Flex,
  Grid,
  GridItem,
  IconButton,
  Image,
  Spinner,
} from '@chakra-ui/react';
import { MdAdd } from 'react-icons/md';
import moment from 'moment';

import GalleryModal from '@app/components/common/modals/gallery';
import { ImageDescriptor } from '@app/api/models';
import { useAlert, useApi } from '@app/hooks';
import { Txt } from '@app/components/common';

type PendingPhoto = {
  filename: string;
  content: string;
  src: string;
};

type SkuPhotosGalleryProps = {
  skuId?: number;
  /** Pending local photos (create mode). Uploaded & linked after SKU is created. */
  pending?: PendingPhoto[];
  onPendingChange?: (pending: PendingPhoto[]) => void;
  readOnly?: boolean;
};

export const SkuPhotosGallery: FC<SkuPhotosGalleryProps> = ({
  skuId,
  pending = [],
  onPendingChange,
  readOnly = false,
}) => {
  const api = useApi();
  const { error, success } = useAlert();

  const [images, setImages] = useState<ImageDescriptor[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [removingImage, setRemovingImage] = useState<number>();
  const [showGallery, setShowGallery] = useState(false);
  const [galleryCurrentIndex, setGalleryCurrentIndex] = useState(0);

  const loadPhotos = useCallback(async () => {
    if (!skuId) {
      setImages([]);
      return;
    }
    setLoading(true);
    try {
      const response = await api.sku.getSkuPhotos(true);
      const links = (response?.data || []).filter(
        (item) => item.sku_id === skuId && item.is_active
      );
      const loaded = await Promise.all(
        links.map(async ({ file_id }) => {
          try {
            const content = await api.file.get(String(file_id));
            return {
              image_pid: file_id,
              last_update: null,
              src: typeof content === 'string' && content.startsWith('data:')
                ? content
                : `data:image/jpeg;base64,${content}`,
            } as ImageDescriptor;
          } catch {
            return {
              image_pid: file_id,
              last_update: null,
              src: undefined,
            } as ImageDescriptor;
          }
        })
      );
      setImages(loaded);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skuId]);

  useEffect(() => {
    loadPhotos();
  }, [loadPhotos]);

  const uploadAndLink = async (file: File, content: string) => {
    if (!skuId) {
      return;
    }
    const uploadResponse = await api.file.upload({
      images: [{ filename: file.name, content }],
    });
    const fileId = uploadResponse?.data?.file_ids?.[0];
    if (!fileId) {
      error('Не удалось загрузить файл');
      return;
    }
    const linkResponse = await api.sku.createSkuPhoto({
      file_id: fileId,
      sku_id: skuId,
      is_active: true,
    });
    if (linkResponse?.code !== 200) {
      error(linkResponse?.message || 'Не удалось привязать фото к SKU');
      return;
    }
    setImages((prev) => [
      ...prev,
      {
        image_pid: fileId,
        last_update: moment().format('YYYY-MM-DD'),
        src: content,
      },
    ]);
    success('Фото добавлено');
  };

  const onAddClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.click();
    input.onchange = (e: Event) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) {
        return;
      }
      setUploading(true);
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = async () => {
        const content = reader.result?.toString();
        if (!content) {
          setUploading(false);
          return;
        }
        try {
          if (skuId) {
            await uploadAndLink(file, content);
          } else {
            onPendingChange?.([
              ...pending,
              { filename: file.name, content, src: content },
            ]);
          }
        } catch (e) {
          error(e.message || 'Ошибка загрузки фото');
        } finally {
          setUploading(false);
        }
      };
      reader.onerror = () => {
        setUploading(false);
        error('Ошибка чтения файла');
      };
    };
  };

  const onDeleteLinked = async (fileId: number) => {
    if (!skuId) {
      return;
    }
    setRemovingImage(fileId);
    try {
      const response = await api.sku.deleteSkuPhoto({
        file_id: fileId,
        sku_id: skuId,
        is_active: false,
      });
      if (response?.code === 200) {
        setImages((prev) => prev.filter((item) => item.image_pid !== fileId));
        success('Фото удалено');
      }
    } catch (e) {
      error(e.message || 'Ошибка удаления фото');
    } finally {
      setRemovingImage(undefined);
    }
  };

  const onDeletePending = (index: number) => {
    onPendingChange?.(pending.filter((_, i) => i !== index));
  };

  const galleryImages: ImageDescriptor[] = [
    ...images,
    ...pending.map((item, index) => ({
      image_pid: -(index + 1),
      last_update: null,
      src: item.src,
    })),
  ];

  return (
    <Flex flexDirection="column" gap={2}>
      <Txt fontWeight={500}>Фотографии</Txt>
      {!skuId && (
        <Txt color="gray.500" fontSize="sm">
          Фото будут загружены после создания SKU
        </Txt>
      )}
      <Grid
        gap={5}
        style={{ position: 'relative' }}
        templateColumns={{ base: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }}
      >
        {loading && (
          <Box
            position="absolute"
            width="100%"
            height={200}
            display="flex"
            justifyContent="center"
            alignItems="center"
            zIndex={1}
          >
            <Spinner />
          </Box>
        )}

        {images.map((image, index) => (
          <GridItem key={`linked_${image.image_pid}`}>
            <Card position="relative">
              {removingImage === image.image_pid && (
                <Box
                  position="absolute"
                  width="100%"
                  height={200}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  zIndex={1}
                  backgroundColor="whiteAlpha.600"
                >
                  <Spinner />
                </Box>
              )}
              <Image
                style={{ height: '200px', cursor: 'pointer' }}
                src={image.src}
                objectFit="cover"
                onClick={() => {
                  setGalleryCurrentIndex(index);
                  setShowGallery(true);
                }}
              />
              {!readOnly && (
                <CardFooter>
                  <Button
                    size="sm"
                    colorScheme="red"
                    variant="ghost"
                    onClick={() => onDeleteLinked(image.image_pid)}
                  >
                    Удалить
                  </Button>
                </CardFooter>
              )}
            </Card>
          </GridItem>
        ))}

        {pending.map((item, index) => (
          <GridItem key={`pending_${index}`}>
            <Card>
              <Image
                style={{ height: '200px', cursor: 'pointer' }}
                src={item.src}
                objectFit="cover"
                onClick={() => {
                  setGalleryCurrentIndex(images.length + index);
                  setShowGallery(true);
                }}
              />
              {!readOnly && (
                <CardFooter>
                  <Button
                    size="sm"
                    colorScheme="red"
                    variant="ghost"
                    onClick={() => onDeletePending(index)}
                  >
                    Удалить
                  </Button>
                </CardFooter>
              )}
            </Card>
          </GridItem>
        ))}

        {!readOnly && (
          <GridItem style={{ opacity: loading ? 0.3 : 1 }}>
            <Box
              minHeight={200}
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
              borderRadius={8}
              border="dotted 1px #AAA"
              position="relative"
            >
              {uploading && (
                <Box
                  position="absolute"
                  width="100%"
                  height={200}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Spinner />
                </Box>
              )}
              {!uploading && (
                <IconButton colorScheme="brand" aria-label="add-photo" onClick={onAddClick}>
                  <MdAdd />
                </IconButton>
              )}
            </Box>
          </GridItem>
        )}
      </Grid>

      <GalleryModal
        images={galleryImages}
        open={showGallery}
        onClose={() => setShowGallery(false)}
        title="Фотографии SKU"
        index={galleryCurrentIndex}
        icon={<div />}
      />
    </Flex>
  );
};

/** Upload pending photos and link them to a newly created SKU. */
export async function uploadPendingSkuPhotos(
  api: ReturnType<typeof useApi>,
  skuId: number,
  pending: PendingPhoto[]
) {
  for (const photo of pending) {
    const uploadResponse = await api.file.upload({
      images: [{ filename: photo.filename, content: photo.content }],
    });
    const fileId = uploadResponse?.data?.file_ids?.[0];
    if (!fileId) {
      continue;
    }
    await api.sku.createSkuPhoto({
      file_id: fileId,
      sku_id: skuId,
      is_active: true,
    });
  }
}

export type { PendingPhoto };
